import React, { useState, useEffect } from 'react';
import { Button, TextInput } from 'components/lib';
import axios from 'axios';

export function NewAccountForm({ onSave, onCancel, editData, entityTerm = 'Client' }) {
  const [accountName, setAccountName] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [monitoringLevel, setMonitoringLevel] = useState('health_score_only');
  const [monitoringLimits, setMonitoringLimits] = useState(null);
  const [, setLoading] = useState(true);

  // Get proper plural form
  const entityTermPlural = entityTerm === 'Company' ? 'Companies' : 
                          entityTerm === 'Client' ? 'Clients' : 
                          `${entityTerm}s`;

  useEffect(() => {
    console.log('editData - ' + JSON.stringify(editData))
    if (editData) {
      setAccountName(editData.client);
      setAccountEmail(editData.email);
      setIsActive(editData.active);
      setMonitoringLevel(editData.monitoring_level === 'Full Monitoring' ? 'full_monitoring' : 'health_score_only');
    }
  }, [editData]);

  useEffect(() => {
    // Fetch monitoring limits
    const fetchLimits = async () => {
      try {
        const response = await axios.get('/api/account/monitoring-limits');
        setMonitoringLimits(response.data);
        
        // Set default to premium monitoring if allowed and not editing
        if (!editData && response.data.canUpgradeToFullMonitoring) {
          setMonitoringLevel('full_monitoring');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching monitoring limits:', error);
        setLoading(false);
      }
    };
    fetchLimits();
  }, [editData]);

  const handleSave = () => {
    const data = {
      id: editData?.id,
      name: accountName,
      email: accountEmail,
      active: isActive,
      account_type: monitoringLevel
    };
    onSave(data);
  };

  const canSelectFullMonitoring = () => {
    if (!monitoringLimits) return false;
    // If editing and already full monitoring, allow keeping it
    if (editData && editData.monitoring_level === 'Full Monitoring') return true;
    return monitoringLimits.canUpgradeToFullMonitoring;
  };

  return (
    <div className="new-account-form">
      <h2>{editData ? `Edit ${entityTerm}` : `Create a New ${entityTerm}`}</h2>
      
      <div className="form-group">
        <label>{entityTerm} Name:</label>
        <TextInput value={accountName} onChange={(name, value) => setAccountName(value)} />
      </div>

      {!editData && ( // Only show monitoring level selection for new accounts
        <div className="form-group">
          <label style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'block' }}>
            Choose monitoring level for this {entityTerm.toLowerCase()}
          </label>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            {/* Health Score Option */}
            <div style={{ 
              border: monitoringLevel === 'health_score_only' ? '2px solid #28a745' : '2px solid #e9ecef',
              borderRadius: '8px', 
              padding: '16px',
              backgroundColor: monitoringLevel === 'health_score_only' ? '#f8fff9' : '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minHeight: '120px',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={() => setMonitoringLevel('health_score_only')}
            >
              <label style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer', margin: 0, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <input
                    type="radio"
                    name="monitoringLevel"
                    value="health_score_only"
                    checked={monitoringLevel === 'health_score_only'}
                    onChange={(e) => setMonitoringLevel(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  <strong style={{ fontSize: '15px', color: '#2d3748' }}>Standard Monitoring</strong>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ 
                    backgroundColor: '#6c757d', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.75em',
                    fontWeight: '500'
                  }}>
                    BASIC
                  </span>
                </div>
                <div style={{ fontSize: '0.85em', color: '#4a5568', lineHeight: '1.3', flex: 1 }}>
                  ✓ Health score tracking<br/>
                  ✓ Basic data alerts<br/>
                </div>
              </label>
            </div>

            {/* Full Monitoring Option */}
            <div style={{ 
              border: monitoringLevel === 'full_monitoring' ? '2px solid #007bff' : '2px solid #e9ecef',
              borderRadius: '8px', 
              padding: '16px',
              backgroundColor: monitoringLevel === 'full_monitoring' ? '#f8f9ff' : canSelectFullMonitoring() ? '#ffffff' : '#f8f9fa',
              cursor: canSelectFullMonitoring() ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: canSelectFullMonitoring() ? 1 : 0.7,
              minHeight: '120px',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={() => canSelectFullMonitoring() && setMonitoringLevel('full_monitoring')}
            >
              <label style={{ display: 'flex', flexDirection: 'column', cursor: canSelectFullMonitoring() ? 'pointer' : 'not-allowed', margin: 0, height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <input
                    type="radio"
                    name="monitoringLevel"
                    value="full_monitoring"
                    checked={monitoringLevel === 'full_monitoring'}
                    onChange={(e) => setMonitoringLevel(e.target.value)}
                    disabled={!canSelectFullMonitoring()}
                    style={{ marginRight: '8px' }}
                  />
                  <strong style={{ fontSize: '15px', color: '#2d3748' }}>Premium Monitoring</strong>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.75em',
                    fontWeight: '500'
                  }}>
                    RECOMMENDED
                  </span>
                </div>
                <div style={{ fontSize: '0.85em', color: '#4a5568', lineHeight: '1.3', flex: 1 }}>
                  ✓ Everything in Standard<br/>
                  ✓ Advanced cleanup tools<br/>
                  ✓ Transaction categorization<br/>
                  ✓ Custom audit rules<br/>
                </div>
                {!canSelectFullMonitoring() && monitoringLimits && (
                  <div style={{ 
                    fontSize: '0.85em', 
                    color: '#856404',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    padding: '8px',
                    marginTop: '8px'
                  }}>
                    <strong>💡 Tip:</strong> You've used all 3 premium slots. You can upgrade {entityTermPlural.toLowerCase()} later from their settings.
                  </div>
                )}
              </label>
            </div>
          </div>

          <div style={{ 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #e9ecef', 
            borderRadius: '6px', 
            padding: '12px', 
            fontSize: '0.85em',
            color: '#6c757d'
          }}>
            {monitoringLimits && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span>{monitoringLimits.reason}</span>
                <span style={{ 
                  backgroundColor: '#e3f2fd', 
                  color: '#1976d2', 
                  padding: '2px 8px', 
                  borderRadius: '12px', 
                  fontSize: '0.9em',
                  fontWeight: '500'
                }}>
                  {monitoringLimits.currentFullyMonitoredCount} used
                </span>
              </div>
            )}
            <div>All {entityTermPlural.toLowerCase()} get health score tracking. You can change monitoring levels anytime.</div>
          </div>
        </div>
      )}

      <div className="button-group" style={{ marginTop: '20px' }}>
        <Button text="Save" action={handleSave} disabled={!accountName.trim()} />
        <Button text="Cancel" action={onCancel} />
      </div>
    </div>
  );
}
